import React from 'react';
import styled from 'styled-components';
import { useTranslation, Trans } from 'react-i18next';
import { CodeIcon, UsersIcon, ClockIcon, RocketIcon, CheckCircleIcon, ToolsIcon } from './IconComponents';

const WhySection = styled.section`
  padding: 5rem 0;
  background-color: var(--light-background);
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 700px;
  margin: 0 auto 3rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: var(--text-color);

  span {
    color: var(--primary-color);
  }
`;

const SectionSubtitle = styled.p`
  color: var(--text-color);
  font-size: 1.1rem;
  line-height: 1.7;
`;

const IntroText = styled.p`
  color: var(--text-color);
  font-size: 1.05rem;
  line-height: 1.8;
  max-width: 900px;
  margin: 1.5rem auto 3rem;
  text-align: center;
  opacity: 0.9;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const StatCard = styled.div`
  text-align: center;
  padding: 1.5rem;
`;

const StatValue = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  color: var(--text-color);
  font-size: 0.95rem;
  line-height: 1.4;
`;

const ReasonsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-bottom: 3rem;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ReasonCard = styled.div`
  background-color: var(--card-background);
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  transition: var(--transition);

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  }
`;

const ReasonIcon = styled.div`
  color: var(--primary-color);
  font-size: 1.8rem;
  margin-bottom: 1rem;
`;

const ReasonTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
  color: var(--text-color);
`;

const ReasonDescription = styled.p`
  font-size: 0.95rem;
  color: var(--text-color);
  line-height: 1.6;
`;

const CTAWrapper = styled.div`
  text-align: center;
`;

const CTAText = styled.p`
  color: var(--text-color);
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
`;

const CTAButton = styled.a`
  display: inline-block;
  background-color: var(--primary-color);
  color: white;
  padding: 1rem 2.5rem;
  border-radius: 6px;
  font-weight: 600;
  font-size: 1.05rem;
  transition: var(--transition);

  &:hover {
    background-color: var(--secondary-color);
    color: var(--dark-background);
    transform: translateY(-2px);
  }
`;

const icons = [CodeIcon, UsersIcon, CheckCircleIcon, RocketIcon, ToolsIcon, ClockIcon];

const WhyByteForge: React.FC = () => {
  const { t } = useTranslation();

  const stats = t('whyByteForge.stats', { returnObjects: true }) as { value: string; label: string }[];
  const reasons = t('whyByteForge.reasons', { returnObjects: true }) as { title: string; description: string }[];

  return (
    <WhySection id="why-byte-forge">
      <Container>
        <SectionHeader>
          <SectionTitle>
            <Trans i18nKey="whyByteForge.title">
              Why <span>Byte Forge</span>
            </Trans>
          </SectionTitle>
          <SectionSubtitle>{t('whyByteForge.subtitle')}</SectionSubtitle>
        </SectionHeader>

        <IntroText>{t('whyByteForge.introText')}</IntroText>

        <StatsGrid>
          {stats.map((stat, index) => (
            <StatCard key={index}>
              <StatValue>{stat.value}</StatValue>
              <StatLabel>{stat.label}</StatLabel>
            </StatCard>
          ))}
        </StatsGrid>

        <ReasonsGrid>
          {reasons.map((reason, index) => {
            const Icon = icons[index % icons.length];
            return (
              <ReasonCard key={index}>
                <ReasonIcon>
                  <Icon />
                </ReasonIcon>
                <ReasonTitle>{reason.title}</ReasonTitle>
                <ReasonDescription>{reason.description}</ReasonDescription>
              </ReasonCard>
            );
          })}
        </ReasonsGrid>

        <CTAWrapper>
          <CTAText>{t('whyByteForge.cta.text')}</CTAText>
          <CTAButton href="/#contact">{t('whyByteForge.cta.button')}</CTAButton>
        </CTAWrapper>
      </Container>
    </WhySection>
  );
};

export default WhyByteForge;
